import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaInstagram, FaTwitter, FaLinkedin } from 'react-icons/fa';

// Home Component
const Home = () => (
  <>
    <Helmet>
      <title>The Boyd Brand | Home</title>
      <meta name="description" content="Welcome to The Boyd Brand, the personal and professional hub of Quaveeon Boyd." />
    </Helmet>
    <motion.section
      className="p-6 mb-10 bg-white dark:bg-gray-800 rounded-lg shadow-md"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className="text-3xl font-semibold mb-4 text-gray-900 dark:text-white">About Me</h2>
      <p className="text-lg text-gray-700 dark:text-gray-300">
        Welcome to The Boyd Brand, the personal and professional hub of Quaveeon Boyd.
      </p>
    </motion.section>
  </>
);

// Podcast Component
const Podcast = () => (
  <>
    <Helmet>
      <title>The Boyd Brand | Podcast</title>
      <meta name="description" content="Explore the latest podcast episodes by Quaveeon Boyd." />
    </Helmet>
    <motion.section
      className="p-6 mb-10 bg-white dark:bg-gray-800 rounded-lg shadow-md"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className="text-3xl font-semibold mb-4 text-gray-900 dark:text-white">Podcast</h2>
      <p className="text-lg text-gray-700 dark:text-gray-300">Explore my latest podcast episodes.</p>
    </motion.section>
  </>
);

// Book Component
const Book = () => (
  <>
    <Helmet>
      <title>The Boyd Brand | Book</title>
      <meta name="description" content="Discover the latest books by Quaveeon Boyd." />
    </Helmet>
    <motion.section
      className="p-6 mb-10 bg-white dark:bg-gray-800 rounded-lg shadow-md"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className="text-3xl font-semibold mb-4 text-gray-900 dark:text-white">Book</h2>
      <p className="text-lg text-gray-700 dark:text-gray-300">Check out my latest book releases.</p>
    </motion.section>
  </>
);

// Record Label Component
const RecordLabel = () => (
  <>
    <Helmet>
      <title>The Boyd Brand | Record Label</title>
      <meta name="description" content="Discover artists and music from The Boyd Brand record label." />
    </Helmet>
    <motion.section
      className="p-6 mb-10 bg-white dark:bg-gray-800 rounded-lg shadow-md"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className="text-3xl font-semibold mb-4 text-gray-900 dark:text-white">Record Label</h2>
      <p className="text-lg text-gray-700 dark:text-gray-300">Discover artists and music from my label.</p>
    </motion.section>
  </>
);

// Contact Component with Social Media Links and Email List
const Contact = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscription = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
    }
  };

  return (
    <>
      <Helmet>
        <title>The Boyd Brand | Contact</title>
        <meta name="description" content="Contact Quaveeon Boyd or connect through social media." />
      </Helmet>
      <motion.section
        className="p-6 mb-10 bg-white dark:bg-gray-800 rounded-lg shadow-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-3xl font-semibold mb-4 text-gray-900 dark:text-white">Contact</h2>
        <p className="text-lg text-gray-700 dark:text-gray-300 mb-4">
          Have questions? Reach out through the form below or connect with me on social media!
        </p>
        
        <form className="space-y-4">
          <input type="text" placeholder="Your Name" className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          <input type="email" placeholder="Your Email" className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          <textarea placeholder="Your Message" className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"></textarea>
          <button type="submit" className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600">Send Message</button>
        </form>

        {/* Email Subscription */}
        <div className="mt-8">
          <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Subscribe to Our Newsletter</h3>
          <form onSubmit={handleSubscription} className="flex space-x-2">
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
            <button type="submit" className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600">
              Subscribe
            </button>
          </form>
          {subscribed && <p className="text-green-500 mt-2">Thanks for subscribing!</p>}
        </div>

        {/* Social Media Icons */}
        <div className="flex space-x-4 mt-6 justify-center">
          <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-2xl text-pink-500 hover:text-pink-600">
            <FaInstagram />
          </a>
          <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-2xl text-blue-400 hover:text-blue-500">
            <FaTwitter />
          </a>
          <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-2xl text-blue-600 hover:text-blue-700">
            <FaLinkedin />
          </a>
        </div>
      </motion.section>
    </>
  );
};

// Main App Component with Dark Mode Toggle
function App() {
  const [darkMode, setDarkMode] = useState(false);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  return (
    <Router>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#000000" />
      </Helmet>

      <div className={`${darkMode ? 'dark' : ''}`}>
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 p-4">
          <header className="mb-8 flex justify-between items-center">
            <h1 className="text-4xl font-bold text-center dark:text-white">The Boyd Brand</h1>
            <button
              onClick={toggleDarkMode}
              className="px-4 py-2 bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-white rounded-md hover:bg-gray-400 dark:hover:bg-gray-600"
            >
              {darkMode ? 'Light Mode' : 'Dark Mode'}
            </button>
          </header>

          <nav className="flex justify-center space-x-4 mb-6">
            <Link to="/" className="text-blue-500 hover:underline dark:text-blue-300">Home</Link>
            <Link to="/podcast" className="text-blue-500 hover:underline dark:text-blue-300">Podcast</Link>
            <Link to="/book" className="text-blue-500 hover:underline dark:text-blue-300">Book</Link>
            <Link to="/recordlabel" className="text-blue-500 hover:underline dark:text-blue-300">Record Label</Link>
            <Link to="/contact" className="text-blue-500 hover:underline dark:text-blue-300">Contact</Link>
          </nav>

          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/podcast" element={<Podcast />} />
              <Route path="/book" element={<Book />} />
              <Route path="/recordlabel" element={<RecordLabel />} />
              <Route path="/contact" element={<Contact />} />
            </Routes>
          </main>
        </div>
      </div>
    </Router>
  );
}

export default App;